// Troca o conteúdo de um elemento preservando onde a pessoa estava.
//
// POR QUE ISTO VIROU ARQUIVO:
//   As quinze telas seguem o mesmo padrão — `raiz.innerHTML = render(estado)`
//   depois de cada escrita. É simples, é previsível, e tem um efeito colateral
//   que ninguém vê lendo o código: `innerHTML` destrói o elemento que estava
//   com o foco, e o foco vai para o `<body>`.
//
//   No Mapa do dia, que é onde a recepção passa o dia, isso aparecia assim:
//   digita o prontuário, dá Tab, o `focusout` grava, a resposta volta em
//   200ms, a tela inteira é recriada — e o que já estava sendo digitado no
//   campo seguinte some junto com o cursor. A pessoa descobre olhando para uma
//   tela onde o que ela acabou de digitar não está.
//
//   A correção não é digitar mais devagar: é o redesenho devolver o cursor, a
//   seleção, o texto em andamento e a rolagem para onde estavam.
//
// O QUE É PRESERVADO E O QUE NÃO É:
//   - foco, posição do cursor e seleção do campo focado: sempre.
//   - o texto em andamento do campo FOCADO: sim, ele ganha do que veio do
//     banco. Quem está com o cursor no campo é a autoridade sobre o conteúdo
//     dele; reescrever por baixo do cursor é pior do que mostrar o valor
//     antigo por um instante.
//   - o texto de campos NÃO focados: não. Esse é o valor que o banco acabou de
//     confirmar, e é justamente o que o redesenho existe para mostrar.
//   - rolagem de qualquer contêiner interno: sim. A tabela do mapa rola na
//     horizontal, e voltar para a coluna 1 a cada lançamento é o mesmo tipo de
//     perda, só mais silenciosa.

/** Um elemento identificado de um jeito que sobrevive à troca do innerHTML. */
interface Marca {
  seletor: string;
  /** Qual das ocorrências do seletor, quando ele não é único. */
  indice: number;
}

/**
 * Seletor a partir do que NÃO muda entre dois renders.
 *
 * `id`, `name` e os `data-*` são o que identifica um campo nesta base — a
 * linha do mapa é `data-linha`, o formulário de despesa usa `name`. O valor
 * fica de fora de propósito: ele é justamente a parte que o redesenho muda.
 */
function seletorDe(el: Element): string {
  const id = el.getAttribute('id');
  if (id) return `#${CSS.escape(id)}`;

  let sel = el.tagName.toLowerCase();
  for (const classe of Array.from(el.classList)) sel += `.${CSS.escape(classe)}`;
  for (const attr of Array.from(el.attributes)) {
    if (attr.name === 'name' || attr.name.startsWith('data-')) {
      sel += `[${attr.name}=${CSS.escape(attr.value)}]`;
    }
  }
  return sel;
}

function marcar(raiz: HTMLElement, el: Element): Marca | null {
  let seletor: string;
  try {
    seletor = seletorDe(el);
  } catch {
    return null;
  }
  const indice = Array.from(raiz.querySelectorAll(seletor)).indexOf(el);
  return indice < 0 ? null : { seletor, indice };
}

function acharPelaMarca(raiz: HTMLElement, marca: Marca): HTMLElement | null {
  try {
    return raiz.querySelectorAll<HTMLElement>(marca.seletor)[marca.indice] ?? null;
  } catch {
    return null;
  }
}

/** Campos em que devolver o texto em andamento faz sentido. Um `date` ou um
 *  `checkbox` não têm "texto pela metade" — e `setSelectionRange` recusa os
 *  dois. */
const TIPOS_DE_TEXTO = new Set(['text', 'search', 'tel', 'url', 'email', 'password']);

function ehCampoDeTexto(el: Element): el is HTMLInputElement | HTMLTextAreaElement {
  if (el instanceof HTMLTextAreaElement) return true;
  return el instanceof HTMLInputElement && TIPOS_DE_TEXTO.has(el.type);
}

interface Foco {
  marca: Marca;
  texto: string | null;
  inicio: number | null;
  fim: number | null;
}

function capturarFoco(raiz: HTMLElement): Foco | null {
  const ativo = document.activeElement;
  if (!(ativo instanceof HTMLElement) || !raiz.contains(ativo)) return null;
  const marca = marcar(raiz, ativo);
  if (!marca) return null;

  if (!ehCampoDeTexto(ativo)) return { marca, texto: null, inicio: null, fim: null };
  return {
    marca,
    texto: ativo.value,
    inicio: ativo.selectionStart,
    fim: ativo.selectionEnd,
  };
}

function restaurarFoco(raiz: HTMLElement, foco: Foco): void {
  const alvo = acharPelaMarca(raiz, foco.marca);
  if (!alvo) return;

  // preventScroll porque o foco volta para onde já estava: deixar o navegador
  // rolar até ele moveria a tela sem motivo.
  alvo.focus({ preventScroll: true });

  if (foco.texto === null || !ehCampoDeTexto(alvo)) return;
  if (alvo.value !== foco.texto) alvo.value = foco.texto;
  if (foco.inicio !== null && foco.fim !== null) {
    try {
      alvo.setSelectionRange(foco.inicio, foco.fim);
    } catch {
      // Campo que não aceita seleção. O foco, que é o que importa, já voltou.
    }
  }
}

interface Rolagem {
  marca: Marca;
  topo: number;
  esquerda: number;
}

function capturarRolagem(raiz: HTMLElement): Rolagem[] {
  const capturadas: Rolagem[] = [];
  for (const el of Array.from(raiz.querySelectorAll<HTMLElement>('*'))) {
    if (el.scrollTop === 0 && el.scrollLeft === 0) continue;
    const marca = marcar(raiz, el);
    if (marca) capturadas.push({ marca, topo: el.scrollTop, esquerda: el.scrollLeft });
  }
  return capturadas;
}

/**
 * Substitui o conteúdo de `raiz` por `html`, devolvendo foco, cursor e rolagem.
 *
 * Use no lugar de `raiz.innerHTML = render(estado)`. Quando não há nada focado
 * dentro de `raiz` — o caso do primeiro desenho — o custo é uma leitura de
 * `document.activeElement` e nada mais.
 */
export function pintar(raiz: HTMLElement, html: string): void {
  const foco = capturarFoco(raiz);
  // A rolagem é capturada mesmo sem foco: em Sincronização e no Funil a pessoa
  // clica numa linha da tabela, que não é focável, e a tabela rolada voltaria
  // para o começo do mesmo jeito.
  const rolagens = capturarRolagem(raiz);

  raiz.innerHTML = html;

  for (const r of rolagens) {
    const el = acharPelaMarca(raiz, r.marca);
    if (!el) continue;
    el.scrollTop = r.topo;
    el.scrollLeft = r.esquerda;
  }
  if (foco) restaurarFoco(raiz, foco);
}
